import { type Currency } from "./models/currency";
import type { ValuesOf } from "./models/enums";

export function randomString(len: number): string {
    const arr = new Uint8Array((len || 40) / 2);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, dec => `0${dec.toString(16)}`.substr(-2)).join("");
}

export function sha256(plain: string): Promise<ArrayBuffer> {
    const encoder = new TextEncoder();
    const data = encoder.encode(plain);
    return window.crypto.subtle.digest("SHA-256", data);
}

export function base64urlencode(a: ArrayBuffer): string {
    const uint8Array = new Uint8Array(a);
    return btoa(String.fromCharCode(...uint8Array))
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, "");
}

export function hexToRgb(hex: string): [number, number, number] {
    const bigint = parseInt(hex.slice(1), 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return[r , g , b]
}

export function rgbToHex([r, g, b]: [number, number, number]): string {
    const componentToHex = (c: number) => {
        const hex = c.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
    }
    return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

export function getQueryObject(str: string): Record<string, unknown> {
    return str
        .replace("?", "")
        .split("&")
        .map(x => x.split("="))
        .reduce((cumulus: Record<string, string>, next) => {
            cumulus[next[0]] = decodeURIComponent(next[1]);
            return cumulus;
        }, {});
}

export function sleep(delay = 0): Promise<unknown> {
    return new Promise(resolve => {
        setTimeout(resolve, delay);
    });
}

export function assert(condition: unknown, msg: string): asserts condition {
    if (!condition) {
        throw new Error(msg);
    }
}

export function capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

export function round(value: number, decimalPlaces = 2): number {
    return Math.round(value * 10 ** decimalPlaces) / 10 ** decimalPlaces;
}

export function normalize(text: string): string {
    return text
        ? text
              .replace(/[^\s\p{Letter}0-9&./]+/giu, " ")
              .replace(/[\s]{2,}/g, "")
              .trim()
        : text;
}

export function extract<T extends Record<string, unknown>, K extends keyof T>(obj: T, prop: K): T[K] {
    const value = obj[prop];
    delete obj[prop];
    return value;
}

export function nullif<T>(src: T, val: T, compare: (x: T, y: T) => boolean = (x, y) => x === y): T | null {
    return compare(src, val) ? null : src;
}

export const money = (amount: number, currency: ValuesOf<typeof Currency>, digits = 2): string => {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency }).format(round(amount, digits));
};

export function isValidBarcode(value: string) {
    // We only allow correct length barcodes
    if (!value.match(/^(\d{8}|\d{12,14})$/)) {
        return false;
    }

    const paddedValue = value.padStart(14, "0");

    let result = 0;
    for (let i = 0; i < paddedValue.length - 1; i += 1) {
        result += Number.parseInt(paddedValue.charAt(i), 10) * (i % 2 === 0 ? 3 : 1);
    }

    return (10 - (result % 10)) % 10 === Number.parseInt(paddedValue.charAt(13), 10);
}

export function rgbToRgba(rgb: string, alpha: number) {
    return rgb.replace("rgb", "rgba").replace(")", `, ${alpha})`);
}

export function parsePoint(value: string): [number, number] {
    // Example input: "(1.2,3.4)"
    const match = value.match(/\((-?\d+(\.\d+)?),\s*(-?\d+(\.\d+)?)\)/);
    if (!match) {
        throw new Error(`Invalid point format: ${value}`);
    }

    return [parseFloat(match[1]), parseFloat(match[3])];
}